import React, {useEffect, useState} from "react";

// Components
import {Button, Col, Modal, QRCode, Row} from "antd";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import {IconContext} from "react-icons";
import {AiOutlineCloudUpload} from "react-icons/ai";
import {FaArrowLeft, FaRegFile, FaVideo} from "react-icons/fa";
import {ImCancelCircle} from "react-icons/im";
import {MdAddAPhoto} from "react-icons/md";
import {useNavigate, useParams} from "react-router-dom";
import FaceSmileFront from "../../../assets/face-smile-front.png";
import FaceSmileLeft from "../../../assets/face-smile-left.png";
import FaceSmileRight from "../../../assets/face-smile-right.png";
import FaceStraightFront from "../../../assets/face-straight-front.png";
import FaceStraightLeft from "../../../assets/face-straight-left.png";
import FaceStraightRight from "../../../assets/face-straight-right.png";
import FaceNoFeeling from "../../../assets/naturalface.png";
import Glasses from "../../../assets/removeglass.png";
import FemaleFigure from "../../../assets/pullhair2.png";
import FaceAlign from "../../../assets/direct.png";
import EnhancedCameraModal from "../../../components/CameraInstructionsModal/EnhancedCameraModal";
import SurveyFaceCameraDrawer from "../../../components/Camera/SurveyFaceCameraDrawer";
import {DashboardWrapper} from "../../../components/DashboardWrapper/DashboardWrapper.styles";
import {uploadPatientImage} from "../../../utils/api-lists";
import {getPatient} from "../../../utils/api-lists/patient.api";
import {GeneralBody} from "../../../widgets/GeneralBody";
import {Header} from "../styles";


const FileUploadPage = () => {
    const navigate = useNavigate();
    const {patientId} = useParams();

    const {t} = useTranslation("common");

    const [file, setFile] = useState();
    const [fileURL, setFileURL] = useState();
    const [uploadProgress, setUploadProgress] = useState(0);
    const [openInMobileModal, setOpenInMobileModal] = useState(false);
    const [patient, setPatient] = useState();

    const currentOrigin = window.location.origin.includes('localhost') ? 'https://www.glacies.ai' : window.location.origin;
    const [beforeUploadModal, setBeforeUploadModal] = useState({isOpen: false, isVideo: false});
    const [befeoreVideoModal, setBeforeVideoModal] = useState(false);
    const [enhancedCameraVisible, setEnhancedCameraVisible] = useState(false);
    // New camera drawer state
    const [cameraDrawerVisible, setCameraDrawerVisible] = useState(false);
    const [cameraMode, setCameraMode] = useState('photo'); // 'photo' | 'video'

    useEffect(() => {
        getPatient(patientId)
            .then((data) => {
                setPatient(data);
            })
            .catch((error) => console.log(error.message));
        document.title = "Visual analysis: File upload";
    }, [patientId]);

    function handleChange(event) {
        setUploadProgress(0);
        const selectedFile = event.target.files[0];
        setFile(selectedFile);
        const url = URL.createObjectURL(selectedFile);
        setFileURL(url);
    }

    function handleEnhancedCameraCapture(file) {
        setFile(file);
        const url = URL.createObjectURL(file);
        setFileURL(url);
        setEnhancedCameraVisible(false);
    }

    // New unified onCapture handler from SurveyFaceCameraDrawer
    const handleCameraCapture = (capturedFile) => {
        setFile(capturedFile);
        try {
            const url = URL.createObjectURL(capturedFile);
            setFileURL(url);
        } catch (e) {
        }
        setCameraDrawerVisible(false);
    };

    function handleSubmit(event) {
        event.preventDefault();
        if (!file) {
            toast.error(t('no_content_captured_yet'));
            return;
        }
        if (!patient) {
            toast.error('Patient data not loaded yet');
            return;
        }
        const date = new Date().toISOString();
        const fileExt = file.name.substring(file.name.lastIndexOf(".") + 1);
        const fileName = `${patient.first_name}_${patient.last_name}_${date}.${fileExt}`;

        const config = {
            onUploadProgress: function (progressEvent) {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
                setUploadProgress(percentCompleted);
            },
        };
        toast
            .promise(uploadPatientImage(patientId, file, fileName, config), {
                loading: "Uploading...",
                success: "Upload successful!",
                error: (e) => `Upload failed ${e.response.data.message}`,
            })
            .then((data) => {
                navigate(`/patient-cabinet/${patientId}/analysis`, {
                    state: {
                        file_open_last: true,
                    },
                    replace: true,
                });
            })
            .catch((e) => {
            });
    }

    const openMobileModal = () => {
        setOpenInMobileModal(!openInMobileModal);
    }

    return (
        <DashboardWrapper>
            <Header>
                <IconContext.Provider value={{style: {cursor: "pointer"}}}>
                    <FaArrowLeft onClick={() => navigate(-1)}/>
                </IconContext.Provider>
                <h3>{t("visual_analysis_upload_file")}</h3>
            </Header>
            <GeneralBody>
                <div
                    className="flex flex-wrap lg:flex-nowrap w-full max-w-6xl bg-opacity-5 rounded-xl shadow-lg overflow-hidden">
                    {/* Left Side (Buttons) */}
                    <div className="fileSelection"></div>
                    <div
                        className="w-full lg:w-1/4 p-6 flex flex-col justify-center items-center gap-6 bg-[rgba(88,80,109,0.30)] rounded-t-xl">
                        {!file && (
                            <>
                                {/* Choose File */}
                                <label
                                    className="min-w-[150px] max-w-[180px] flex items-center justify-center gap-2 w-full py-3 text-sm font-bold bg-blue-500 text-white rounded-lg cursor-pointer text-center transition duration-300 transform hover:bg-blue-600">
                                    <FaRegFile className="text-2xl"/>
                                    {t("choose_a_file")}
                                    <input
                                        name="file"
                                        id="file"
                                        type="file"
                                        onChange={handleChange}
                                        className="hidden"
                                    />
                                </label>
                                {/* Capture Photo */}
                                <button
                                    className="min-w-[150px] max-w-[180px] flex items-center justify-center gap-2 w-full py-3 text-sm font-bold bg-blue-500 text-white rounded-lg transition duration-300 transform hover:bg-blue-600"
                                    onClick={() => setBeforeUploadModal({isOpen: true, isVideo: false})}
                                >
                                    <MdAddAPhoto className="text-2xl"/>
                                    {t("capture_photo")}
                                </button>
                                {/* Record Video */}
                                <button
                                    className="min-w-[150px] max-w-[180px] flex items-center justify-center gap-2 w-full py-3 text-sm font-bold bg-blue-500 text-white rounded-lg transition duration-300 transform hover:bg-blue-600"
                                    onClick={() => setBeforeVideoModal(true)}
                                >
                                    <FaVideo className="text-2xl"/> {t("record_video", {defaultValue: "Record Video"})}
                                </button>
                                {/* Enhanced camera kept optional */}
                            </>
                        )}
                        {file && (
                            <>
                                {/* ...existing upload & cancel buttons... */}
                                <button
                                    onClick={handleSubmit}
                                    className="min-w-[150px] max-w-[180px] flex justify-center items-center gap-2 w-full py-3 text-sm font-bold bg-purple-600 text-white rounded-lg transition duration-300 transform hover:bg-purple-700 mb-10"
                                >
                                    <AiOutlineCloudUpload className="text-2xl"/>
                                    {t("upload")}
                                </button>
                                <button
                                    className="min-w-[150px] max-w-[180px] flex justify-center items-center gap-2 w-full py-3 text-sm font-bold bg-red-500 text-white rounded-lg transition duration-300 transform hover:bg-red-600"
                                    onClick={() => setFile()}
                                >
                                    <ImCancelCircle className="text-2xl"/> {t("cancel")}
                                </button>
                                {uploadProgress > 0 && (
                                    <progress value={uploadProgress} max="100"></progress>
                                )}
                            </>
                        )}
                    </div>

                    {/* Right Side (Preview) */}
                    <div
                        className="w-full lg:w-3/4 h-[400px] p-6 flex justify-center items-center bg-[#121530] rounded-r-xl">
                        <div
                            id="preview"
                            className="flex justify-center items-center w-full h-full"
                        >
                            {file && file.type.startsWith("image") && fileURL && (
                                <img
                                    className="max-w-full max-h-full object-fit"
                                    src={fileURL}
                                    alt="preview"
                                />
                            )}
                            {file && file.type.startsWith("video") && fileURL && (
                                <video
                                    controls
                                    className="max-w-full max-h-full object-fit"
                                    src={fileURL}
                                />
                            )}
                            {!file && (
                                <p className="text-lg font-semibold text-center">
                                    {t("no_content_captured_yet")}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </GeneralBody>
            {/* Photo preparation modal */}
            <Modal
                title={t("prepare_skin_analysis")}
                className="whiteModal"
                open={beforeUploadModal.isOpen}
                onCancel={() => setBeforeUploadModal({...beforeUploadModal, isOpen: false})}
                width={700}
                footer={null}
            >
                <>
                    <Row justify={"start"}>
                        <h3>{t("follow_steps")}</h3>
                    </Row>
                    <Row justify="center" gutter={[32, 16]} className="mt-4">
                        <Col span={12} className="flex flex-col items-center justify-center text-center h-full">
                            <img src={Glasses} alt="NoGlasses" className="w-[100px] h-[100px] mb-2"/>
                            <p>{t("remove_glasses")}</p>
                        </Col>
                        <Col span={12} className="flex flex-col items-center justify-center text-center h-full">
                            <img src={FemaleFigure} alt="PullHairBack" className="w-[100px] h-[100px] mb-2"/>
                            <p>{t("pull_hair_back")}</p>
                        </Col>
                    </Row>

                    <Row justify="center" gutter={[32, 16]} className="mt-8">
                        <Col span={12} className="flex flex-col items-center justify-center text-center h-full">
                            <img src={FaceAlign} alt="FaceCamera" className="w-[100px] h-[100px] mb-2"/>
                            <p>{t("face_camera")}</p>
                        </Col>
                        <Col span={12} className="flex flex-col items-center justify-center text-center h-full">
                            <img src={FaceNoFeeling} alt="NoExpression" className="w-[100px] h-[100px] mb-2"/>
                            <p>{t("natural_expression")}</p>
                        </Col>
                    </Row>
                </>


                <div className="mt-4 flex justify-end">
                    <Button
                        type="primary"
                        style={{
                            backgroundColor: "red",
                            borderColor: "red",
                            marginRight: "10px",
                        }}
                        onClick={() =>
                            setBeforeUploadModal({...beforeUploadModal, isOpen: false})
                        }
                    >
                        {t("cancel")}
                    </Button>
                    <Button
                        type="primary"
                        style={{
                            backgroundColor: "#8e44ad",
                            borderColor: "#8e44ad",
                            marginRight: "10px",
                        }}
                        onClick={() => {
                            setBeforeUploadModal({...beforeUploadModal, isOpen: false});
                            setCameraMode('photo');
                            setCameraDrawerVisible(true);
                        }}
                    >
                        {t("continue")}
                    </Button>
                </div>
            </Modal>
            {/* Video instruction modal */}
            <Modal
                title={t("prepare_skin_analysis")}
                className="custom-dark-modal"
                open={befeoreVideoModal}
                onCancel={() => setBeforeVideoModal(false)}
                width={700}
                footer={null}>

                <>
                    <Row justify={"start"}>
                        <h3>{t("video_instruction")}</h3>
                    </Row>

                    <Row justify={"start"} className="mt-8">
                        <h3>{t("neutral_face")}</h3>
                    </Row>

                    <Row justify="center" gutter={[32, 16]} className="mt-4">
                        <Col span={8} className="flex flex-col items-center justify-center text-center h-full">
                            <img src={FaceStraightFront} alt="FaceStraightFront" className="w-[100px] h-[100px] mb-2"/>
                            <p>{t("front_view")}</p>
                        </Col>
                        <Col span={8} className="flex flex-col items-center justify-center text-center h-full">
                            <img src={FaceStraightRight} alt="FaceStraightRight" className="w-[100px] h-[100px] mb-2"/>
                            <p>{t("slightly_right")}</p>
                        </Col>
                        <Col span={8} className="flex flex-col items-center justify-center text-center h-full">
                            <img src={FaceStraightLeft} alt="FaceStraightLeft" className="w-[100px] h-[100px] mb-2"/>
                            <p>{t("slightly_left")}</p>
                        </Col>
                    </Row>

                    <Row justify={"start"} className="mt-8">
                        <h3>{t("smiling_face")}</h3>
                    </Row>

                    <Row justify="center" gutter={[32, 16]} className="mt-8">
                        <Col span={8} className="flex flex-col items-center justify-center text-center h-full">
                            <img src={FaceSmileFront} alt="FaceSmileFront" className="w-[100px] h-[100px] mb-2"/>
                            <p>{t("front_view")}</p>
                        </Col>
                        <Col span={8} className="flex flex-col items-center justify-center text-center h-full">
                            <img src={FaceSmileLeft} alt="NoExpression" className="w-[100px] h-[100px] mb-2"/>
                            <p>{t("slightly_right")}</p>
                        </Col>
                        <Col span={8} className="flex flex-col items-center justify-center text-center h-full">
                            <img src={FaceSmileRight} alt="PullHairBack" className="w-[100px] h-[100px] mb-2"/>
                            <p>{t("slightly_left")}</p>
                        </Col>
                    </Row>
                </>


                <div className="mt-4 flex justify-end">
                    <Button
                        type="primary"
                        style={{
                            backgroundColor: "red",
                            borderColor: "red",
                            marginRight: "10px",
                        }}
                        onClick={() => {
                            setBeforeVideoModal(false);
                        }}
                    >
                        {t("cancel")}
                    </Button>
                    <Button
                        type="primary"
                        style={{backgroundColor: "#8e44ad", borderColor: "#8e44ad"}}
                        onClick={() => {
                            setBeforeVideoModal(false);
                            setCameraMode('video');
                            setCameraDrawerVisible(true);
                        }}
                    >
                        {t("continue")}
                    </Button>

                </div>
            </Modal>

            <Modal
                title={t("open_in_mobile")}
                open={openInMobileModal}
                footer={null}
                onCancel={openMobileModal}
                width={{
                    lg: "1060px",
                    xl: "1060px",
                    xxl: "1060px",
                }}
                // styles={{body: {height: '90%'}}}
                destroyOnClose={true}
            >
                <div style={{
                    maxHeight: "80vh",
                    minHeight: "400px",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}>
                    <QRCode
                        errorLevel="L"
                        value={`${currentOrigin}/deeplink?action=camera&patientId=${patientId}`}
                        size={300}
                    />
                    <div className="flex flex-row gap-3 justify-center items-center mt-4">
                        <a href="https://apps.apple.com/app/id1551353775" className="storeLink">
                            <img
                                style={{
                                    width: 300,
                                    padding: 30
                                }}
                                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us"
                                alt="Download on the App Store"/>
                        </a>
                        <a
                            href='https://play.google.com/store/apps/details?id=com.stagescycling.stages'
                            className="storeLink">
                            <img alt='Get it on Google Play'
                                 style={{
                                     width: 300,
                                 }}
                                 src='https://play.google.com/intl/en_us/badges/images/generic/en_badge_web_generic.png'/>
                        </a>
                    </div>
                </div>
            </Modal>
            {/* SurveyFaceCameraDrawer integration */}
            <SurveyFaceCameraDrawer
                visible={cameraDrawerVisible}
                onClose={() => setCameraDrawerVisible(false)}
                onCapture={handleCameraCapture}
                height="100%"
                mode={cameraMode}
            />
            <EnhancedCameraModal
                visible={enhancedCameraVisible}
                onClose={() => setEnhancedCameraVisible(false)}
                onCapture={handleEnhancedCameraCapture}
                showInstructions={true}
                mode="fileUpload"
            />
        </DashboardWrapper>
    );
};

export default FileUploadPage;
